<template>
    <div class="content">
      <div class="content-wrap">
        <h3>{{ t('add-role').toUpperCase() }}</h3>
        <div class="card-body">
          <Form
            ref="form"
            @submit="onSubmit"
            :validation-schema="schema"
            v-slot="{ errors, isSubmitting }"
            :keep-values="true"
          >
            <!-- Dane podstawowe -->
            <div class="column d-flex justify-content-center role-data">
              <div class="col-sm-6">
                <div class="form-group row">
                  <label class="col-sm-4 col-form-label">{{ t('name2') }}:</label>
                  <div class="col-sm-8">
                    <Field
                      name="name"
                      as="input"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !!errors?.name }"
                    />
                    <div class="invalid-feedback">{{ errors.name }}</div>
                  </div>
                </div>
              </div>
  
              <div class="col-sm-6">
                <div class="form-group row">
                  <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                  <div class="col-sm-8">
                    <Field
                      name="description"
                      as="textarea"
                      class="form-control"
                      :class="{ 'is-invalid': !!errors?.description }"
                    />
                    <div class="invalid-feedback">{{ errors.description }}</div>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Uprawnienia (kafelki jak w edycji) -->
            <div class="column d-flex justify-content-center role-data">
              <div class="form-group row w-100">
                <div class="col-sm-12">
                  <!-- ZAZNACZONE -->
                  <p class="mb-2 text-muted" v-if="selectedPermissionsDetails.length">{{ t('selected-label') }}:</p>
                  <div class="roles-tiles mb-3" v-if="selectedPermissionsDetails.length">
                    <label
                      v-for="perm in selectedPermissionsDetails"
                      :key="'sel-'+perm.name"
                      class="role-tile selected"
                    >
                      <Field
                        name="permissions"
                        as="input"
                        type="checkbox"
                        class="sr-only"
                        :value="perm.name"
                        v-model="selectedPermissions"
                        :aria-checked="selectedPermissions.includes(perm.name)"
                        :aria-label="perm.name"
                      />
                      <div class="role-title">{{ perm.name }}</div>
                      <div class="role-desc" v-if="perm.description">{{ perm.description }}</div>
                    </label>
                  </div>
  
                  <!-- DOSTĘPNE -->
                  <p class="mb-2 text-muted">{{ t('available-label') }}:</p>
                  <div
                    class="roles-tiles"
                    :class="{ 'is-invalid': !!formErrors.permissions }"
                    role="group"
                    aria-label="permissions"
                  >
                    <label
                      v-for="perm in availablePermissions"
                      :key="'avail-'+perm.name"
                      class="role-tile"
                      :class="{
                        selected: selectedPermissions.includes(perm.name),
                        'role-tile-locked': !canGrant(perm.name),
                      }"
                      :title="!canGrant(perm.name)
                        ? t('permission-grant-blocked-tile')
                        : null"
                    >
                      <Field
                        name="permissions"
                        as="input"
                        type="checkbox"
                        class="sr-only"
                        :value="perm.name"
                        v-model="selectedPermissions"
                        :disabled="!canGrant(perm.name)"
                        :aria-checked="selectedPermissions.includes(perm.name)"
                        :aria-label="perm.name"
                      />
                      <div class="role-title">{{ perm.name }}</div>
                      <div class="role-desc" v-if="perm.description">{{ perm.description }}</div>
                    </label>
                  </div>
  
                  <div class="invalid-feedback d-block">{{ formErrors.permissions }}</div>
                </div>
              </div>
            </div>
  
            <!-- Akcje / komunikaty -->
            <div class="form-group row d-flex flex-column align-items-center process">
              <div class="col-auto" v-if="processError">
                <div class="process-error form-control">{{ processError }}</div>
              </div>
              <div class="col-auto" v-if="processSuccess">
                <div class="process-success form-control">{{ processSuccess }}</div>
              </div>
              <div class="col-auto">
                <button type="button" @click="goBack" class="btn button-add-user">
                  {{ t('cancel') }}
                </button>
                <button class="btn button-save" :disabled="isSubmitting">
                  <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                  {{ t('save').toUpperCase() }}
                </button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import moment from "moment";
  import { Form, Field } from "vee-validate";
  import * as Yup from "yup";
  import { useI18n } from "vue-i18n";
  
  moment.locale("pl");
  const { t } = useI18n();
  
  const schema = Yup.object({
    name: Yup.string().required(t('user-username-required')),
    description: Yup.string(),
    permissions: Yup.array().of(Yup.string()).min(1, t('user-permissions-required')),
  });
  </script>
  
  <script>
  import RolesService from "@/services/Roles";
  import { useUserStore } from "@/stores/user.store";

  export default {
    name: "AddRole",
    data() {
      return {
        rolesAvailable: [],          // lista „ról” zwracanych z API (zawierają tablice permissions)
        catalogPermissions: [],      // pełny katalog uprawnień (access.role) — także nieprzypisane do grup
        selectedPermissions: [],     // zaznaczone uprawnienia (nazwy, np. "ROLE_27")
        processError: "",
        processSuccess: "",
        formErrors: {},              // lokalny holder na errors.permissions, żeby pokazać pod siatką
      };
    },
    created() {
      // pobierz listę ról (źródło master listy uprawnień)
      RolesService.getRolesList()
        .then((data) => {
          this.rolesAvailable = Array.isArray(data) ? data : [];
        })
        .catch((e) => (this.processError = e?.message || String(e)));
      // pobierz pełny katalog uprawnień (żeby pokazać także nowo dodane, jeszcze nieprzypisane do grup)
      RolesService.getAllPermissions()
        .then((data) => {
          this.catalogPermissions = Array.isArray(data) ? data : [];
        })
        .catch((e) => console.error("Nie udało się pobrać katalogu uprawnień:", e));
    },
    methods: {
      goBack() {
        this.$emit("cancel-add-role");
      },
      // wyciągnij numer z nazwy (ROLE_10 -> 10) do sortowania
      getPermId(p) {
        if (p && typeof p.id === "number") return p.id;
        const m = (p?.name ?? "").match(/\d+/);
        const n = m ? parseInt(m[0], 10) : NaN;
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      },
      sortByPermId(a, b) {
        const da = this.getPermId(a);
        const db = this.getPermId(b);
        if (da !== db) return da - db;
        return (a?.name ?? "").localeCompare(b?.name ?? "");
      },
      // Czy aktualny admin może nadać dane uprawnienie (ROLE_17/18/19 wymagają posiadania).
      canGrant(name) {
        return this.userStore.canGrantPermission(name);
      },
      onSubmit(values) {
        this.processSuccess = "";
        this.processError = "";
        this.formErrors = {};

        // jeśli Vee-Validate nie wstrzyknął permissions (np. user nic nie dotknął),
        // to weź z local state
        const perms = Array.from(new Set(values.permissions ?? this.selectedPermissions));

        if (!perms.length) {
          // pokaż błąd w tym samym miejscu co w edycji
          this.formErrors.permissions = this.$t?.('user-permissions-required') || 'Wybierz co najmniej jedno uprawnienie.';
          return;
        }

        // Defensywnie: backend i tak zwróci 403, ale zatrzymajmy żądanie wcześniej z
        // czytelnym komunikatem.
        const blocked = perms.filter((name) => !this.userStore.canGrantPermission(name));
        if (blocked.length) {
          this.processError = this.$t('permission-grant-blocked-error', {
            perms: blocked.join(', '),
          });
          return;
        }
  
        const payload = {
          name: (values.name || "").trim(),
          description: (values.description || "").trim(),
          permissions: perms, // ["ROLE_10", "ROLE_11", ...]
        };
  
        RolesService.addRole(payload)
          .then(() => {
            this.processSuccess = "Utworzono rolę.";
            // reset formu + stanów
            this.selectedPermissions = [];
            this.$refs.form?.resetForm();
            this.$emit('role-added');
            this.$router.push('/role-group-management');
          })
          .catch((err) => {
            console.error(err);
            this.processError = err?.response?.data?.message || err?.message || "Błąd zapisu.";
          });
      },
    },
    computed: {
      // Store przez getter — w SFC z `<script setup>` Options-API `setup()` nie jest
      // uruchamiany, więc `return { userStore }` daje `undefined` w `this`. Pinia
      // jest singletonem, więc wywołanie przy każdym dostępie nic nie kosztuje.
      userStore() {
        return useUserStore();
      },
      // unikalna, posortowana master-lista uprawnień — przycięta do zakresu szczebla usera
      // (`manageablePermissions`). Nowa grupa powstaje od zera, więc nie ma tu uprawnień
      // „zastanych": wszystko, co widać, user może nadać.
      //
      // Filtrowanie jest konieczne mimo przycięcia katalogu po stronie BE, bo listę zasilają
      // też uprawnienia wyciągnięte z istniejących grup (GET /roles) — tam admin zobaczyłby
      // np. ROLE_17 pochodzące z grupy operatora.
      allPermissions() {
        const map = new Map();
        // Uprawnienia z istniejących grup (mają opisy).
        (this.rolesAvailable || []).forEach(r => {
          (r.permissions || []).forEach(p => {
            const obj = (typeof p === "string") ? { name: p, description: "" } : p;
            if (!map.has(obj.name)) map.set(obj.name, obj);
          });
        });
        // Pełny katalog uprawnień (dokłada także nowe, jeszcze nieprzypisane do żadnej grupy).
        (this.catalogPermissions || []).forEach(p => {
          const obj = (typeof p === "string") ? { name: p, description: "" } : p;
          if (!map.has(obj.name)) map.set(obj.name, obj);
        });

        const manageable = new Set(this.userStore.manageablePermissions());

        return Array.from(map.values())
          .filter(p => manageable.has(p.name))
          .sort(this.sortByPermId);
      },
      selectedPermissionsDetails() {
        const set = new Set(this.selectedPermissions);
        return this.allPermissions.filter(p => set.has(p.name));
      },
      availablePermissions() {
        const set = new Set(this.selectedPermissions);
        return this.allPermissions.filter(p => !set.has(p.name));
      },
    },
  };
  </script>
  
<style scoped>

h3 { 
    padding: 15px 25px;
    background-color: var(--surface-primary);
}

input[type="checkbox"] {
    margin-top: 13px;
}

.sr-only {
    position: absolute !important;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; border: 0;
}

.roles-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--spacing-md);
}

.role-tile {
    border: 1px solid var(--border-default);
    border-radius: var(--panel-radius);
    padding: 12px 14px;
    background: var(--surface-primary);
    cursor: pointer;
    user-select: none;
    transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}

.role-tile:hover {
    border-color: var(--border-default);
    background: var(--surface-primary);
}

.role-tile.selected {
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 2px var(--accent-focus-ring);
    background: var(--accent-blue-soft);
}

.role-tile.role-tile-locked {
    opacity: .55;
    background: var(--surface-secondary);
    cursor: not-allowed;
    border-style: dashed;
}

.role-tile.role-tile-locked:hover {
    border-color: var(--border-default);
    background: var(--surface-secondary);
}

.role-title { font-weight: var(--font-weight-medium); line-height: 1.2; }
.role-desc { margin-top: 6px; font-size: var(--font-size-sm); color: var(--text-muted); }
.roles-tiles.is-invalid { border: 1px dashed var(--status-error); border-radius: var(--panel-radius); padding: 8px; }
.col-form-label { text-align: right; }
.process-error { color: darkred; margin-bottom: 1rem; }
.process-success { color: var(--status-ok); margin-bottom: 1rem; }
.content { width: 100%; min-height: 270px; margin-bottom: 20px; }
.card-body .column { padding: 20px 30px; }
.card-body .column > div { padding: 5px 20px; }
.role-data { border-bottom: 1px solid var(--border-default); }
.invalid-feedback { font-size: var(--font-size-xs); }
.button-add-user { margin-top: 1rem; margin-bottom: 1rem; padding: .5rem 2rem; margin-right: .5rem; }
.button-save { margin-top: 1rem; margin-bottom: 1rem; padding: .5rem 2rem; border: 1px solid var(--status-ok); }
.button-save:hover {
  background-color: var(--status-ok) !important;
  border: 1px solid var(--status-ok) !important;
}
</style>
  
